/**
 * "Sign in with Apple" button — required by App Store Review Guideline 4.8
 * whenever we offer any third-party social login on iOS.
 *
 * Only renders on native iOS. On the web and on Android it returns null so we
 * don't force users onto Apple ID authentication where it's not required.
 *
 * Uses `@capacitor-community/apple-sign-in` to invoke Apple's native
 * `AuthenticationServices` flow. The resulting `identityToken` is verified
 * server-side against Apple's JWKS by `POST /api/auth/apple/login`.
 */
import { useState } from 'react';
import { SignInWithApple } from '@capacitor-community/apple-sign-in';
import { Button } from './ui/button';
import { toast } from 'sonner';
import { useAuth } from '../lib/auth';
import { isNativeApp, getPlatform } from '../lib/capacitor';
import { useNavigate } from 'react-router-dom';
import { useI18n } from '../lib/i18n';

// Generate a cryptographically random nonce (32 bytes → 64 hex chars) so the
// server can bind the response to this specific request.
const randomNonce = (bytes = 32) => {
  const arr = new Uint8Array(bytes);
  window.crypto.getRandomValues(arr);
  return Array.from(arr, (b) => b.toString(16).padStart(2, '0')).join('');
};

export const AppleSignInButton = ({ mode = 'login', onSuccess, className = '' }) => {
  const [loading, setLoading] = useState(false);
  const { appleLogin } = useAuth();
  const navigate = useNavigate();
  const { language } = useI18n();

  // Apple sign in only on native iOS. Hidden everywhere else.
  if (!isNativeApp() || getPlatform() !== 'ios') return null;

  const handleClick = async () => {
    setLoading(true);
    try {
      const nonce = randomNonce();
      const state = randomNonce(16);

      const result = await SignInWithApple.authorize({
        clientId: 'app.bookvia.bookvia',
        redirectURI: '', // ignored by native iOS flow
        scopes: 'email name',
        state,
        nonce,
      });

      // Plugin can return the fields either directly or under .response
      // depending on the installed major version — normalize both shapes.
      const r = result?.response ?? result ?? {};
      if (!r.identityToken) {
        throw new Error('Apple did not return an identity token');
      }

      await appleLogin({
        identity_token: r.identityToken,
        authorization_code: r.authorizationCode || null,
        nonce,
        email: r.email || null,
        given_name: r.givenName || null,
        family_name: r.familyName || null,
      });

      toast.success(
        language === 'es'
          ? 'Sesión iniciada con Apple'
          : 'Signed in with Apple'
      );

      if (onSuccess) {
        onSuccess();
      } else {
        navigate('/');
      }
    } catch (err) {
      // Cancelled by the user — silent, no toast.
      const msg = String(err?.message || err || '');
      if (/1000|1001|cancel/i.test(msg)) {
        setLoading(false);
        return;
      }
      console.error('[AppleSignIn] failed:', err);
      toast.error(
        language === 'es'
          ? 'Error al iniciar con Apple'
          : 'Apple sign-in error'
      );
    } finally {
      setLoading(false);
    }
  };

  const label = mode === 'register'
    ? (language === 'es' ? 'Registrarse con Apple' : 'Sign up with Apple')
    : (language === 'es' ? 'Continuar con Apple' : 'Continue with Apple');

  return (
    <Button
      type="button"
      onClick={handleClick}
      disabled={loading}
      data-testid="apple-signin-btn"
      className={
        'w-full h-12 text-base font-medium gap-2 bg-black text-white hover:bg-black/90 active:bg-black/80 border border-black ' +
        className
      }
    >
      {/* Apple logo — Human Interface Guidelines single-color mark.
          Path centered within a 24x24 viewBox so it renders correctly at
          any size without stretching. */}
      <svg
        aria-hidden="true"
        viewBox="0 0 24 24"
        width="18"
        height="18"
        fill="currentColor"
      >
        <path d="M17.05 20.28c-.98.95-2.05.8-3.08.35-1.09-.46-2.09-.48-3.24 0-1.44.62-2.2.44-3.06-.35C2.79 15.25 3.51 7.59 9.05 7.31c1.35.07 2.29.74 3.08.8 1.18-.24 2.31-.93 3.57-.84 1.51.12 2.65.72 3.4 1.8-3.12 1.87-2.38 5.98.48 7.13-.57 1.5-1.31 2.99-2.55 4.08zM12.03 7.25c-.15-2.23 1.66-4.07 3.74-4.25.29 2.58-2.34 4.5-3.74 4.25z" />
      </svg>
      {loading
        ? (language === 'es' ? 'Iniciando...' : 'Signing in...')
        : label}
    </Button>
  );
};

export default AppleSignInButton;
